import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "../../../apiClient";
import { ArticleDto } from "../../../models/article/dto/article.dto";
import { UpdateArticleInputDto } from "../../../models/article/dto/update-article.dto";
import { useSelectedOrganization } from "../../organization/hooks/useSelectedOrganization";
import { GET_ARTICLE_QUERY_KEY } from "./useGetArticle";

export type UpdateArticleWithImageInput = {
    articleData: Omit<UpdateArticleInputDto, 'imageUrl'>;
    imageFile?: File;
};

export const useUpdateArticle = () => {
    const client = useApiClient();
    const queryClient = useQueryClient();
    const { selectedOrganization } = useSelectedOrganization();
    return useMutation({
        mutationFn: ({ id, input }: { id: string; input: UpdateArticleInputDto | UpdateArticleWithImageInput }) => {
            // Check if it's the new format with image file
            if ('articleData' in input) {
                const formData = new FormData();

                // Append all article data fields
                Object.entries(input.articleData).forEach(([key, value]) => {
                    if (value !== undefined && value !== null) {
                        if (Array.isArray(value)) {
                            formData.append(key, JSON.stringify(value));
                        } else {
                            formData.append(key, value.toString());
                        }
                    }
                });

                // Append image file if provided
                if (input.imageFile) {
                    formData.append('image', input.imageFile);
                }

                return client.fetchApiWithFormData<ArticleDto>({
                    endpoint: `articles/${id}/with-image`,
                    method: 'PUT',
                    formData
                });
            } else {
                // Legacy format - regular JSON request
                return client.fetchApi<ArticleDto, UpdateArticleInputDto>({
                    endpoint: `articles/${id}`,
                    method: 'PUT',
                    body: input
                });
            }
        },
        onSuccess: (data) => {
            queryClient.setQueryData(GET_ARTICLE_QUERY_KEY(data.id), data);
            queryClient.invalidateQueries({ queryKey: ['articles', 'list', selectedOrganization?.id || ''] });
        },
    });
};
